import { fetchAuthSession } from 'aws-amplify/auth'

const API_URL = import.meta.env.VITE_API_URL

export type DashboardType =
  | 'company'
  | 'leadership-pulse'
  | 'leadership-revenue'
  | 'leadership-margins'
  | 'leadership-cash'
  | 'leadership-compare'
  | 'leadership-sales'
  | 'leadership-people'
  | 'leadership-marketing'
  | 'scorecard-finance'
  | 'scorecard-leadership'
  | 'scorecard-operations'
  | 'scorecard-ps'
  | 'scorecard-sales'

export type UserRole = 'admin' | 'leadership' | 'employee'

// Cognito groups (source of truth for authorization)
export type CognitoGroup =
  | 'admin'
  | 'leadership'
  | 'company'
  | 'dept-engineering'
  | 'dept-operations'
  | 'dept-professional-services'
  | 'dept-rnd'

export interface UserRoleResponse {
  role: UserRole
  email: string
  display_name: string | null
  provisioned: boolean
}

export interface User {
  id: number
  cognito_sub: string
  email: string
  display_name: string | null
  role: UserRole  // DB role (deprecated)
  groups: CognitoGroup[]  // Cognito groups (source of truth)
  created_at: string
  last_login_at: string | null
  updated_at: string
}

export interface UsersResponse {
  users: User[]
  count: number
}

async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString()
  if (!token) {
    throw new Error('No auth token available')
  }
  return token
}

export interface EmbedParams {
  timeframe?: '1' | '3' | '12'
  comparison?: 'mom' | 'qoq' | 'yoy'
  budget_cat?: 'budget' | 'reforecast'
}

export async function getEmbedUrl(dashboard?: DashboardType, params?: EmbedParams): Promise<string> {
  const token = await getAuthToken()

  const url = new URL(`${API_URL}/embed-url`)
  if (dashboard) {
    url.searchParams.set('dashboard', dashboard)
  }
  // Pass additional params based on dashboard
  if (params?.timeframe) {
    url.searchParams.set('timeframe', params.timeframe)
  }
  if (params?.comparison) {
    url.searchParams.set('comparison', params.comparison)
  }
  if (params?.budget_cat) {
    url.searchParams.set('budget_cat', params.budget_cat)
  }

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  const data = await response.json()
  return data.embedUrl
}

export interface StrategySession {
  url: string
  audience: string
  expiresAt: number
}

// Mints CloudFront signed cookies scoped to the caller's strategy audience.
// credentials: 'include' is required so the browser stores the cookies for
// jarvis.rhythmic.dev; navigation to session.url then carries them.
export async function getStrategySession(): Promise<StrategySession> {
  const token = await getAuthToken()

  const response = await fetch(`${API_URL}/strategy-session`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  return response.json()
}

export type KillSignalStatus = 'green' | 'amber' | 'tripped' | 'no-data' | 'expired'

export interface StaleInitiative {
  id: string
  owner_slack_id: string
  days_stale: number
}

// How to render a signal's value and threshold. Set by the evaluator.
export type SignalFormat = 'pct' | 'currency' | 'count'

export interface KillSignalDetails {
  statement?: string
  owner?: string
  format?: SignalFormat
  stale?: StaleInitiative[]
}

export interface KillSignal {
  signal_id: string
  evaluated_at: string
  status: KillSignalStatus
  value: number | null
  threshold: number
  deadline: string | null
  details: KillSignalDetails | null
}

export interface TargetStatus {
  quarter: string
  metric: string
  target_min: number
  target_max: number | null
  actual: number
  evaluated_at: string
}

export interface KillSignalsResponse {
  signals: KillSignal[]
  targets: TargetStatus[]
  generated_at: string
}

export async function getKillSignals(): Promise<KillSignalsResponse> {
  const token = await getAuthToken()

  const response = await fetch(`${API_URL}/kill-signals`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  return response.json()
}

export async function getUserRole(): Promise<UserRoleResponse> {
  const token = await getAuthToken()

  const response = await fetch(`${API_URL}/me/role`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  return response.json()
}

export async function getUsers(): Promise<UsersResponse> {
  const token = await getAuthToken()

  const response = await fetch(`${API_URL}/users`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  return response.json()
}

export async function updateUserRole(userId: number, role: UserRole): Promise<User> {
  const token = await getAuthToken()

  const response = await fetch(`${API_URL}/users/${userId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  const data = await response.json()
  return data.user
}

export async function updateUserGroup(userId: number, group: CognitoGroup): Promise<User> {
  const token = await getAuthToken()

  const response = await fetch(`${API_URL}/users/${userId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ group }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `API error: ${response.status}`)
  }

  const data = await response.json()
  return data.user
}
